/**
 * GateNegocioScreen — Quality Gate 1 · Escopo de Negócio (gate de FASE).
 *
 * Porta o Gate de Negócio do protótipo: as personas de negócio (PO · PM · Analista)
 * validam FUNCIONALIDADES, CANVAS DE NEGÓCIO (greenfield, da Ideação) e as TELAS do
 * Design Studio, com parecer, SLA e auditoria hash-chain. Aprova/Rejeita/Waiver via
 * PATCH /projects/{id}/journey-gates/negocio (auditável). Não depende de execução.
 * Lê ?project=<id> (fallback: projeto mais recente).
 */
import { C } from '@/theme/palette';
import { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useProjects, useIdeation } from '@/api/projects';
import { useJourneyGate, useDecideJourneyGate } from '@/api/techScope';
import {
  useDesignStudio, useScopeAudit, useComputeScopeAudit, useSaveClarifications,
} from '@/api/designStudio';
import { GateExportBar } from './GateExportBar';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

// Aprovadores/personas/SLA do Gate de Negócio (espelha o protótipo · GATE_DEFS.negocio).
const GATE_DEF = {
  num: 1, label: 'Escopo de Negócio', sla: '24h',
  personas: 'Product Owner · Product Manager · Analista de Requisitos',
  approvers: [
    { name: 'Maria Souza', role: 'Product Owner', avatar: 'MS' },
    { name: 'João Mendes', role: 'Product Manager', avatar: 'JM' },
    { name: 'Ana Lima', role: 'Analista de Requisitos', avatar: 'AL' },
  ],
};

const ST = {
  approved: { label: 'APROVADO', fg: C.ok, bg: 'rgba(1,230,228,0.12)' },
  rejected: { label: 'REJEITADO', fg: C.bad, bg: 'rgba(255,51,0,0.12)' },
  pending:  { label: 'AGUARDANDO APROVAÇÃO', fg: C.warn, bg: 'rgba(255,176,32,0.12)' },
};

function Card({ title, right, accent = C.accent, children }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
          letterSpacing: '0.1em', color: accent }}>{title}</h4>
        {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 14px',
      border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface }}>
      <span style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{value}</span>
    </div>
  );
}

// Canvas de Negócio (greenfield) montado a partir da Ideação.
function BusinessCanvas({ ideation }) {
  const blocks = [
    ['Ideia central', ideation.main_idea],
    ['Valor de negócio', ideation.business_value],
    ['Personas', ideation.personas],
    ['Stakeholders', ideation.stakeholders],
    ['Requisitos não-funcionais', ideation.non_functional_requirements],
    ['Regras de negócio', ideation.business_rules],
  ].filter(([, v]) => v && String(v).trim());
  if (!blocks.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
      {blocks.map(([title, v]) => (
        <div key={title} style={{ padding: 12, background: C.black, borderRadius: 8, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: C.muted,
            textTransform: 'uppercase', marginBottom: 6 }}>{title}</div>
          <div style={{ fontSize: 12.5, color: C.dim, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{String(v)}</div>
        </div>
      ))}
    </div>
  );
}

// Auditoria AGNÓSTICA (IA olhos frescos): valida escopo inicial (docs do cliente) × telas,
// aponta fora-de-contexto/dúvidas/inconsistências, e deixa responder+excluir → volta à ideação.
function ScopeAuditPanel({ projectId, navigate }) {
  const { data: cached } = useScopeAudit(projectId);
  const run = useComputeScopeAudit(projectId);
  const save = useSaveClarifications(projectId);
  const [answers, setAnswers] = useState({});    // { idx: texto }
  const [excluded, setExcluded] = useState({});  // { idx: bool } dos out_of_context
  const a = run.data || cached;
  const computed = a?.computed;
  const pct = a?.understood_pct ?? 0;
  const ooc = a?.out_of_context || [];
  const doubts = a?.doubts || [];
  const inc = a?.inconsistencies || [];
  const pctColor = pct >= 90 ? C.ok : pct >= 70 ? C.warn : C.bad;

  const doSave = async () => {
    const ans = doubts
      .map((d, i) => ({ question: d.question, answer: (answers[i] || '').trim() }))
      .filter((x) => x.answer);
    const exc = ooc
      .map((o, i) => (excluded[i] ? { item: o.item, reason: o.why } : null))
      .filter(Boolean);
    if (!ans.length && !exc.length) return;
    try {
      await save.mutateAsync({ answers: ans, exclusions: exc });
      navigate(`/journey?step=ideation&project=${projectId}`);
    } catch (_e) { /* erro exibido abaixo */ }
  };

  return (
    <Card title="Auditoria agnóstica do escopo (olhos frescos · sem memória)" accent={C.digital}
      right={
        <button onClick={() => run.mutate()} disabled={run.isPending}
          style={{ cursor: run.isPending ? 'default' : 'pointer', background: 'transparent',
            color: C.digital, border: `1px solid ${C.digital}66`, borderRadius: 8,
            padding: '9px 16px', fontSize: 14, fontWeight: 700, opacity: run.isPending ? 0.6 : 1 }}>
          {run.isPending ? 'Auditando…' : computed ? '↻ Reauditar' : '🔍 Rodar auditoria'}
        </button>
      }>
      <p style={{ color: C.dim, fontSize: 15, margin: '0 0 18px', lineHeight: 1.75 }}>
        Uma IA independente lê os <strong style={{ color: C.text }}>documentos originais do cliente</strong> e as
        telas geradas — <strong>sem usar nada do que já está salvo</strong> — para confirmar se o escopo foi
        entendido por completo: o que ficou <strong>fora de contexto</strong>, quais <strong>dúvidas</strong> e
        <strong> inconsistências</strong> existem nos documentos.
      </p>
      {run.isError && <div style={{ color: C.bad, fontSize: 14.5, marginBottom: 12 }}>{String(run.error?.title || run.error?.message || run.error)}</div>}
      {!computed && !run.isPending && (
        <div style={{ fontSize: 14.5, color: C.muted }}>Clique em “Rodar auditoria” para validar o escopo antes de aprovar.</div>
      )}
      {computed && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 22 }}>
            <div style={{ fontSize: 46, fontWeight: 800, color: pctColor, lineHeight: 1 }}>{pct}%</div>
            <div style={{ fontSize: 15.5, color: C.dim, flex: 1, minWidth: 240, lineHeight: 1.6 }}>
              <strong style={{ color: a?.coherent ? C.ok : C.warn, fontSize: 16 }}>
                {a?.coherent ? 'Coerente com o escopo' : 'Atenção: pontos a resolver'}
              </strong> — {a?.verdict}
            </div>
          </div>

          {ooc.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.warn, marginBottom: 12 }}>
                ⚠ FORA DE CONTEXTO ({ooc.length}) — marque para EXCLUIR do escopo (não gera código/tela):
              </div>
              {ooc.map((o, i) => (
                <label key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 16px',
                  border: `1px solid ${C.border}`, borderRadius: 10, background: C.black, marginBottom: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!excluded[i]}
                    onChange={(e) => setExcluded((p) => ({ ...p, [i]: e.target.checked }))}
                    style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
                  <span style={{ fontSize: 14.5, color: C.dim, lineHeight: 1.6 }}>
                    <strong style={{ color: C.text }}>{o.item}</strong>
                    {o.where ? ` (${o.where})` : ''} — {o.why}
                  </span>
                </label>
              ))}
            </div>
          )}

          {doubts.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginBottom: 12 }}>
                ❓ DÚVIDAS NOS DOCUMENTOS ({doubts.length}) — responda para enriquecer o escopo:
              </div>
              {doubts.map((db, i) => (
                <div key={i} style={{ padding: '14px 16px', border: `1px solid ${C.border}`, borderRadius: 10,
                  background: C.black, marginBottom: 12 }}>
                  <div style={{ fontSize: 15, color: C.text, fontWeight: 600, lineHeight: 1.5 }}>{db.question}</div>
                  {db.source && <div style={{ fontSize: 13, color: C.muted, margin: '4px 0 8px' }}>fonte: {db.source}</div>}
                  <textarea value={answers[i] || ''} onChange={(e) => setAnswers((p) => ({ ...p, [i]: e.target.value }))}
                    placeholder="Resposta do cliente…" rows={3}
                    style={{ width: '100%', boxSizing: 'border-box', background: C.surface, color: C.text,
                      border: `1px solid ${C.border}`, borderRadius: 8, padding: '11px 13px', fontSize: 14.5,
                      lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }} />
                </div>
              ))}
            </div>
          )}

          {inc.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.bad, marginBottom: 12 }}>
                ✗ INCONSISTÊNCIAS NOS DOCUMENTOS ({inc.length}):
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, color: C.dim, fontSize: 14.5, lineHeight: 1.9 }}>
                {inc.map((x, i) => <li key={i}>{x.description}{Array.isArray(x.refs) && x.refs.length ? ` (${x.refs.join('; ')})` : ''}</li>)}
              </ul>
            </div>
          )}

          {(doubts.length > 0 || ooc.length > 0) && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
              <button onClick={doSave} disabled={save.isPending}
                style={{ cursor: save.isPending ? 'default' : 'pointer', background: C.accent, color: C.black,
                  border: 'none', borderRadius: 9, padding: '12px 20px', fontSize: 15, fontWeight: 700,
                  opacity: save.isPending ? 0.6 : 1 }}>
                {save.isPending ? 'Salvando…' : '💾 Salvar respostas/exclusões + voltar à Ideação'}
              </button>
              <span style={{ fontSize: 13, color: C.muted }}>
                As respostas entram no escopo (re-indexadas) e as exclusões não geram mais nada.
              </span>
            </div>
          )}
          {save.isError && (
            <div style={{ marginTop: 10, color: C.bad, fontSize: 14 }}>
              Falha ao salvar: {String(save.error?.title || save.error?.message || save.error)}
            </div>
          )}
          {save.isSuccess && (
            <div style={{ marginTop: 10, color: C.ok, fontSize: 14 }}>
              ✓ Salvo — escopo atualizado. Redirecionando para a Ideação…
            </div>
          )}
          {computed && ooc.length === 0 && doubts.length === 0 && inc.length === 0 && (
            <div style={{ fontSize: 15, color: C.ok, fontWeight: 600 }}>✓ Nada fora de contexto, sem dúvidas nem inconsistências — escopo entendido.</div>
          )}
        </>
      )}
    </Card>
  );
}

export function GateNegocioScreen({ projectId: propProjectId, onContinue, onBack } = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = propProjectId || urlProject || projects?.[0]?.id || null;
  const project = projects.find((p) => p.id === projectId) || null;

  const { data: gate } = useJourneyGate(projectId, 'negocio');
  const decide = useDecideJourneyGate(projectId, 'negocio');
  const { data: design } = useDesignStudio(projectId);
  const { data: ideation } = useIdeation(projectId);
  const [comment, setComment] = useState('');

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  const status = gate?.status ?? 'pending';
  const s = ST[status] || ST.pending;
  const approved = status === 'approved';

  const screens = design?.screens ?? [];
  const version = design?.version ?? 1;
  const features = Array.isArray(ideation?.features) ? ideation.features : [];
  const isGreenfield = (project?.project_type === 'greenfield') || Boolean(ideation);
  const canvasBlocks = isGreenfield && ideation ? [
    ['Ideia central', ideation.main_idea],
    ['Valor de negócio', ideation.business_value],
    ['Personas', ideation.personas],
    ['Stakeholders', ideation.stakeholders],
    ['Requisitos não-funcionais', ideation.non_functional_requirements],
    ['Regras de negócio', ideation.business_rules],
  ].filter(([, v]) => v && String(v).trim()) : [];
  const hasCanvas = canvasBlocks.length > 0;

  const goDesignStudio = () => navigate(`/journey/design-studio?project=${projectId}`);
  // Embutido no wizard: avança para a próxima etapa (Blueprint) via onContinue.
  // Standalone (/gates/negocio): volta para a Gestão do Projeto.
  const goJourney = () => (onContinue ? onContinue() : navigate(`/project?project=${projectId}`));

  // ── objeto normalizado para exportação (.md / PDF) — GateExportBar ─────────
  const exportGate = {
    num: GATE_DEF.num,
    label: GATE_DEF.label,
    projectName: project?.name,
    projectId,
    description: 'Encerramento do Escopo de Negócio — as personas de negócio validam '
      + 'funcionalidades, canvas de negócio, telas e critérios de aceite antes de avançar '
      + 'para a Tecnologia.',
    status,
    statusLabel: s.label,
    decidedAt: gate?.decidedAt,
    comment: gate?.comment,
    sla: GATE_DEF.sla,
    personas: GATE_DEF.personas,
    approvers: GATE_DEF.approvers.map((a) => ({ ...a, done: approved })),
    sections: [
      features.length ? {
        title: 'Funcionalidades do produto (escopo de negócio)',
        kind: 'list',
        items: features.map((f) => (typeof f === 'string' ? f : (f?.title || JSON.stringify(f)))),
      } : null,
      hasCanvas ? { title: 'Canvas de Negócio (Greenfield · da Ideação)', kind: 'kv', data: canvasBlocks } : null,
      screens.length ? {
        title: 'Telas do Design Studio',
        kind: 'list',
        items: screens.map((sc) => `**${sc.name}**${sc.description ? ` — ${sc.description}` : ''}`),
      } : null,
    ].filter(Boolean),
  };

  return (
    <div className="sp-page" style={{ color: C.text, maxWidth: 980 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>
          🛡 Quality Gate {GATE_DEF.num} — {GATE_DEF.label}
        </h2>
        <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
          color: s.fg, background: s.bg, letterSpacing: '0.04em' }}>{s.label}</span>
        <GateExportBar gate={exportGate} style={{ marginLeft: 'auto' }} />
      </div>
      <p style={{ color: C.dim, marginTop: 8, fontSize: 13.5, maxWidth: '78ch' }}>
        Encerramento do Escopo de Negócio — as personas de negócio validam funcionalidades,
        {hasCanvas ? ' canvas de negócio,' : ''} telas e critérios de aceite antes de avançar para a Tecnologia.
      </p>

      {/* Pills de métricas */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
        <Pill label="Funcionalidades" value={String(features.length || '—')} />
        <Pill label="Telas definidas" value={`${screens.length} · v${version}`} />
        {hasCanvas && <Pill label="Canvas de Negócio" value="Gerado ✓" />}
      </div>

      {gate?.decidedAt && (
        <p style={{ color: C.muted, fontSize: 12.5, marginTop: 12 }}>
          Decidido em {new Date(gate.decidedAt).toLocaleString('pt-BR')}
          {gate.comment ? ` · ${gate.comment}` : ''}
        </p>
      )}
      {decide.isError && (
        <div style={{ marginTop: 12, color: C.bad, fontSize: 13 }}>
          {String(decide.error?.title || decide.error?.message || decide.error)}
        </div>
      )}

      {/* Funcionalidades extraídas */}
      <Card title="Funcionalidades do produto (escopo de negócio)">
        {features.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 16, color: C.dim, fontSize: 13, lineHeight: 1.7 }}>
            {features.map((f, i) => <li key={i}>{typeof f === 'string' ? f : (f?.title || JSON.stringify(f))}</li>)}
          </ul>
        ) : (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            Sem funcionalidades registradas na Ideação ainda. (Em Brownfield, o escopo vem do contexto/discovery.)
          </div>
        )}
      </Card>

      {/* Canvas de Negócio — greenfield */}
      {hasCanvas && (
        <Card title="Canvas de Negócio" accent={C.quantum}
          right={<span style={{ fontSize: 10, fontWeight: 700, color: C.quantum,
            border: `1px solid ${C.quantum}66`, borderRadius: 5, padding: '2px 7px' }}>GREENFIELD · DA IDEAÇÃO</span>}>
          <BusinessCanvas ideation={ideation} />
        </Card>
      )}

      {/* Telas do Design Studio */}
      <Card title="Telas do Design Studio"
        right={<button onClick={goDesignStudio} style={{ cursor: 'pointer', background: 'transparent',
          color: C.accent, border: `1px solid ${C.accent}55`, borderRadius: 7, padding: '5px 11px',
          fontSize: 12 }}>👁 Abrir no Design Studio</button>}>
        {screens.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 }}>
            {screens.map((sc) => (
              <div key={sc.id} style={{ padding: 12, background: C.black, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>◱ {sc.name}</div>
                {sc.description && <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>{sc.description}</div>}
                {Array.isArray(sc.components) && sc.components.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                    {sc.components.slice(0, 4).map((c, i) => (
                      <span key={i} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5,
                        background: 'rgba(1,230,228,0.1)', color: C.accent }}>{c}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: C.muted }}>
            Nenhuma tela ainda — gere o Design Studio antes de aprovar o gate.
          </div>
        )}
      </Card>

      {/* Auditoria agnóstica: escopo inicial (docs do cliente) × telas geradas */}
      <ScopeAuditPanel projectId={projectId} navigate={navigate} />

      {/* Painel de aprovadores + SLA + auditoria + parecer */}
      <div style={{ marginTop: 20, border: `1px solid ${C.border}`, background: C.surface2, borderRadius: 12, padding: 16 }}>
        {approved && (
          <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8,
            background: 'rgba(1,230,228,0.06)', border: `1px solid ${C.accent}44`, fontSize: 12.5, color: C.dim }}>
            <strong style={{ color: C.accent }}>Gate aprovado — fase concluída.</strong> Aprovações registradas
            na trilha de auditoria imutável (hash-chain).
          </div>
        )}
        <div style={{ fontSize: 10, letterSpacing: '0.05em', color: C.muted, fontWeight: 700, marginBottom: 8 }}>
          APROVADORES · {GATE_DEF.personas.toUpperCase()}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          {GATE_DEF.approvers.map((a) => (
            <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 8,
              border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 12px 4px 4px', background: C.black }}>
              <span style={{ width: 26, height: 26, borderRadius: 999, background: approved ? C.accent : C.surface2,
                color: approved ? C.black : C.dim, display: 'grid', placeItems: 'center',
                fontSize: 10, fontWeight: 700 }}>{a.avatar}</span>
              <span style={{ fontSize: 12 }}>
                <span style={{ color: C.text, fontWeight: 600 }}>{a.name}</span>
                <span style={{ color: C.muted }}> · {a.role}</span>
              </span>
              {approved && <span style={{ color: C.ok, fontSize: 12 }}>✓</span>}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          ⏱ SLA: aprovação em até {GATE_DEF.sla} · Escala para backup em 48h · Admin em 72h
        </div>

        <label style={{ fontSize: 12.5, color: C.dim }}>Parecer do aprovador (obrigatório para rejeitar)</label>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
          placeholder="Ex: o requisito #5 precisa de clarificação sobre quais eventos serão publicados…"
          style={{ width: '100%', marginTop: 6, background: C.black, color: C.text,
            border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {onBack && (
            <button onClick={onBack} style={{ background: 'transparent', color: C.dim,
              border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 16px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer' }}>
              ← Voltar
            </button>
          )}
          {approved ? (
            <button onClick={goJourney} style={{ background: C.accent, color: C.black, border: 'none',
              borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Continuar jornada →
            </button>
          ) : (
            <>
              <button onClick={() => decide.mutate({ decision: 'approve', comment })} disabled={decide.isPending}
                style={{ background: C.accent, color: C.black, border: 'none', borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: decide.isPending ? 0.6 : 1 }}>
                {decide.isPending ? 'Registrando…' : 'Aprovar Escopo de Negócio'}
              </button>
              <button onClick={() => decide.mutate({ decision: 'reject', comment })} disabled={decide.isPending}
                style={{ background: 'transparent', color: C.bad, border: `1px solid ${C.bad}`, borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Rejeitar</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
